import React, { useContext, useMemo } from "react";
// hooks
import { useDirectAppAccess } from "../../customHooks/apps/useDirectAppAccess";
import { useGetCatalogueApps } from "../../customHooks/apps/useApps";
// Models
import { AppModel } from "../../models/app";
// context
import { useLocation, useParams } from "react-router-dom";
// components
import Forbidden from "../PermissionedComponents/Forbidden";
import IframePage from "../Iframe/IframePage";
// maui
import { Spin } from "@findemdev/maui";
// helpers
import { decodeString } from "@findemdev/findem-helpers";
// constants
import {
  DUMMY_APPS,
  RAAS_INTERNAL_PORTAL_ID,
} from "../../constants/mockData/catalogueApps";
// types
import { IApp } from "../../types/app";
import { resolveInstalledAppUrl } from "../../utils/installedAppUrl";
import UserContext from "../../contexts/UserContext";
import { Permission } from "../../common/constants/permissions";
import { hasUserAgentAccess } from "../../common/utils/accessUtils";

interface IInstalledAppProps {
  hideLayoutBars: boolean;
}

/**
 * Direct app access rules describe exceptions to the normal installed-app
 * catalog flow. They are for deep links that should open even when the app is
 * not returned by useGetCatalogueApps because the user has an approved
 * alternate ACL.
 *
 * Example: Pathways may be hidden from the apps list for a user who does not
 * have Permission.PathwaysApp, but a RAAS People List link can still deep-link
 * into Pathways when the user passes the FM Agent gate.
 *
 * The hook still accepts the app's own permission. canAccess only adds another
 * valid role for that specific app permission. Keep this list explicit because
 * adding a rule changes direct-link behavior for that app.
 */
const DIRECT_APP_ACCESS_RULES = [
  {
    canAccess: hasUserAgentAccess,
    permission: Permission.PathwaysApp,
  },
];

const InstalledApp: React.FC<IInstalledAppProps> = ({ hideLayoutBars }) => {
  const { id: paramId } = useParams<{ id?: string }>();
  const { search } = useLocation();
  const userModel = useContext(UserContext);

  const { isLoading, apps } = useGetCatalogueApps();

  // camalize the id to match react component example: cred-builder -> CredBuilder
  // Render the component based on the id and if iframe url is not provided
  // const appComponentStr = paramId?.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join('');
  // const appComponent = appComponentStr ? require(`../${appComponentStr}/${appComponentStr}`).default : null;

  const appComponent: React.FC | null = React.useMemo(() => {
    const _appId = paramId?.replaceAll("-", "_");
    if (
      _appId === RAAS_INTERNAL_PORTAL_ID &&
      !userModel.canAccessRaasInternalPortal()
    ) {
      return null;
    }

    const _app = DUMMY_APPS.find((app: IApp) => app.id === _appId);
    return _app?.component ? (_app.component as React.FC) : null;
  }, [paramId, userModel]);

  const installedAppIframeUrl = useMemo(() => {
    const launchedApp = apps?.find(
      (app: AppModel) => app.getValue().id === paramId,
    );

    return resolveInstalledAppUrl({
      search,
      launchUrl: launchedApp?.getValue().launchUrl,
    });
  }, [apps, paramId, search]);

  const {
    iframeUrl: directAppIframeUrl,
    shouldShowAccessDenied: shouldShowDirectAppAccessDenied,
  } = useDirectAppAccess({
    accessRules: DIRECT_APP_ACCESS_RULES,
    roles: userModel.getUser()?.roles,
    routeAppId: paramId,
    search,
  });

  const iframeUrl = installedAppIframeUrl || directAppIframeUrl;

  // add post message listener to handle sidebar collapse
  React.useLayoutEffect(() => {
    window.addEventListener("message", (event) => {
      if (
        event.data?.type === "searchPageMsg" ||
        event.data?.type === "searchesPageMsg"
      ) {
        // add url query param to main page
        const { pathName } = event.data;
        if (pathName) {
          const url = new URL(
            `${window.location.origin}${window.location.pathname}`,
          );
          url.searchParams.set("url", decodeString(pathName));
          window.history.pushState({}, "", decodeString(url.toString()));
        }
      }
    });
  }, []);

  return (
    <>
      {!isLoading && iframeUrl && (
        <IframePage hideLayoutBars={hideLayoutBars} iframeRoute={iframeUrl} />
      )}
      {appComponent && <>{React.createElement(appComponent)}</>}
      {!isLoading && shouldShowDirectAppAccessDenied && !iframeUrl && (
        <Forbidden />
      )}
      {isLoading && (
        <div>
          <Spin />
        </div>
      )}
    </>
  );
};

export default InstalledApp;
